'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import Paragraph from '@/components/Paragraph';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import Wire from './Wire';
import MobileWire from './MobileWire';
import Hook from './Hook';
import Padlock from './Padlock';
import FruitsArray from './FruitsArray';
import { constructSnippet } from './MutableFruits.helpers';

const MOBILE_CUTOFF = 650;

function MutableFruits({
  codeOutput,
}: {
  codeOutput: React.ReactNode;
}) {
  const id = React.useId();
  const [numOfFruits, setNumOfFruits] = React.useState(1);

  const choiceIndex = 0;

  const choiceRefs = React.useRef<{
    [index: number]: HTMLDivElement;
  }>({});

  const codeSnippet = constructSnippet(numOfFruits);

  return (
    <>
      <Wrapper>
        <Label>
          {' '}
          <Prefix>const</Prefix>
          fruits
        </Label>

        <WireWrapper>
          <Wire
            choiceRefs={choiceRefs}
            choiceIndex={choiceIndex}
            connectionStyle="locked"
          />
          <FloatingPadlock />
        </WireWrapper>
        <MobileWireWrapper>
          <MobileWire />
          <MobilePadlock />
        </MobileWireWrapper>

        <ChoicesWrapper>
          <Choice
            ref={(elem) => {
              // @ts-ignore
              choiceRefs.current[choiceIndex] = elem;
            }}
            htmlFor={`choice-${id}`}
          >
            <Background>
              <BackHook />
            </Background>
            <ChoiceLabel>
              <FruitsArray
                numOfFruits={numOfFruits}
                increment={() => setNumOfFruits(numOfFruits + 1)}
                decrement={() => setNumOfFruits(numOfFruits - 1)}
              />
            </ChoiceLabel>

            <FrontHook />
          </Choice>
        </ChoicesWrapper>
      </Wrapper>
      <SubsequentCopy>
        <Paragraph>
          In code, this would look something like this:
        </Paragraph>
        <CodeSnippetProvider lang="js" code={codeSnippet}>
          {codeOutput}
        </CodeSnippetProvider>
      </SubsequentCopy>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 48px;
  isolation: isolate;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    flex-direction: column;
    gap: 0px;
  }
`;

const Label = styled.div`
  position: relative;
  font-size: 1.25rem;
  font-family: var(--font-family-mono);
  align-self: flex-start;
  margin-top: 74px;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    margin-top: 16px;
    align-self: center;
    margin-bottom: 16px;
  }
`;

const Prefix = styled.span`
  position: absolute;
  top: 5px;
  left: 0;
  transform: translateY(-100%);
  font-size: 0.75rem;
  color: var(--color-gray-700);
`;

const WireWrapper = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  align-self: stretch;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: none;
  }
`;

const MobileWireWrapper = styled.div`
  display: none;
  position: relative;
  width: fit-content;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: block;
  }
`;

const FloatingPadlock = styled(Padlock)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
`;

const MobilePadlock = styled(Padlock)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  margin: auto;
`;

const ChoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Choice = styled.label`
  position: relative;
  color: black;
  color-scheme: light;
  border-radius: 4px;
  padding: 16px;
  font-family: var(--font-family-mono);
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: white;
  /* prettier-ignore */
  filter:
    drop-shadow(0px 1px 2px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 2px 4px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 4px 8px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 8px 16px hsl(0deg 0% 0% / 0.1));
`;

const ChoiceLabel = styled.div`
  position: relative;
`;

const BackHook = styled(Hook)`
  position: absolute;
  z-index: 1;
  top: 12px;
  margin: auto;
  left: -21px;
  width: 21px;
  height: 33px;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: none;
  }
`;

const FrontHook = styled(BackHook)`
  z-index: 3;
  clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0% 100%);
`;

const SubsequentCopy = styled.div`
  margin-top: 64px;
`;

export default MutableFruits;
